import {
  EMPTY_PERIOD_FILTER,
  getPeriodRange,
  PeriodFilterValue,
  periodPresetLabelKey,
} from '@/common/periodFilter';
import { track } from '@/common/track';
import { Button } from '@/components/Button';
import {
  StudentCoursesDialog,
  StudentCoursesDialogType,
} from '@/components/dialogs/StudentCoursesDialog';
import { StatCard } from '@/components/StatCard';
import { StudentAccordions } from '@/components/StudentAccordions';
import { CourseStudentRow, CourseStudentsTable } from '@/components/table/CourseStudentsTable';
import { TablePlaceholder } from '@/components/TablePlaceholder';
import { TextField } from '@/components/TextField';
import { TrainingStudentFilterDrawer } from '@/components/TrainingStudentFilterDrawer';
import useDebounce from '@/hooks/useDebounce';
import { useExportTrainingCertificates } from '@/hooks/useExportTrainingCertificates';
import { useExportTrainingStudents } from '@/hooks/useExportTrainingStudents';
import { useExportCourseProgressReport } from '@/hooks/useExportCourseProgressReport';
import { useGetStudentStats } from '@/hooks/useGetStudentStats';
import { useGetTrainingStudents } from '@/hooks/useGetTrainingStudents';
import { useAppData } from '@/providers/AppDataProvider';
import { useLocale } from '@/providers/LocaleProvider';
import { useManageLearning } from '@/providers/ManageLearningProvider';
import AddIcon from '@/resources/images/icons-add.svg';
import ChevronDownIcon from '@/resources/images/icons-chevron-down.svg';
import DownloadIcon from '@/resources/images/icons-download.svg';
import FilterIcon from '@/resources/images/icons-filter.svg';
import RemoveIcon from '@/resources/images/icons-remove.svg';
import SearchIcon from '@/resources/images/icons-search.svg';
import { KLAYO_COLORS } from '@/themes';
import { BaseSelectorOption } from '@/types/components/selector.types';
import { IStudent, IStudentSummaryResponse } from '@/types/views/student.types';
import { getDateFormatPattern } from '@/utilities/formatDate';
import {
  Box,
  Chip,
  Grid,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import { format } from 'date-fns';
import { ConfirmationDialog, ManageTrainingEvent, Theme } from 'klayowebshared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// The five LearnUpon statuses PARTITION the enrolments: every enrolment lands in at most one of
// them (an unrecognised status lands in none).
const STAT_CARDS: { key: keyof IStudentSummaryResponse['summary']; labelKey: string }[] = [
  { key: 'completed', labelKey: 'manage.completed' },
  { key: 'inProgress', labelKey: 'manage.inProgress' },
  { key: 'notStarted', labelKey: 'manage.notStarted' },
  { key: 'failed', labelKey: 'manage.failed' },
  { key: 'pending', labelKey: 'manage.pending' },
  {
    key: 'overdue',
    labelKey: 'manage.overdue',
  },
];

// Overdue is NOT a sixth status: it cross-cuts the five above, so a past-due failure is counted in
// both and these numbers no longer sum to Enrolled. It sits apart, with a caption saying so — as a
// peer chip beside an Enrolled total it reads as an arithmetic error.
const OVERDUE_CARD: (typeof STAT_CARDS)[number] = { key: 'overdue', labelKey: 'manage.overdue' };

// The four date-range filters, in display order. Keyed to TrainingStudentFilters fields.
const DATE_FILTER_FIELDS: {
  key: 'enrolmentDate' | 'completedDate' | 'dueDate' | 'expiryDate';
  labelKey: string;
}[] = [
  { key: 'enrolmentDate', labelKey: 'manage.enrolmentDate' },
  { key: 'completedDate', labelKey: 'manage.completedDate' },
  { key: 'dueDate', labelKey: 'manage.dueDate' },
  { key: 'expiryDate', labelKey: 'manage.expiryDate' },
];

const mapStudentToRow = (student: IStudent): CourseStudentRow => ({
  // This grid is one row per STUDENT (the counters), so the employee id is a valid row key here —
  // unlike the per-attempt grids, where several rows share one employee.
  id: student.employeeId,
  employeeId: student.employeeId,
  name: student.fullName,
  firstName: student.firstName,
  lastName: student.lastName,
  fullName: student.fullName,
  jobTitle: student.jobTitle,
  enrolledCount: student.enrolled,
  completedCount: student.completed,
  inProgressCount: student.inProgress,
  notStartedCount: student.notStarted,
  failedCount: student.failed,
  overdueCount: student.overdue,
  pendingCount: student.pending,
});

interface ManageTrainingStudentsProps {
  theme?: typeof Theme;
}

export const ManageTrainingStudents = ({ theme }: ManageTrainingStudentsProps) => {
  const { translate } = useLocale();
  const muiTheme = useTheme();
  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const { user } = useAppData();
  const { manageStudentsFilterState, dispatchManageStudentsFilter, courseFiltersData } =
    useManageLearning();
  const navigate = useNavigate();

  const { search, paginationModel, sortBy, orderBy, statuses, filters } = manageStudentsFilterState;
  const searchDebounce = useDebounce(search.trim(), 500);

  useEffect(() => {
    track(ManageTrainingEvent.StudentsViewed);
  }, []);

  useEffect(() => {
    if (searchDebounce) track(ManageTrainingEvent.StudentsSearch, { text: searchDebounce });
  }, [searchDebounce]);

  const sortModel: GridSortModel = useMemo(
    () => [{ field: sortBy, sort: orderBy }],
    [sortBy, orderBy]
  );

  const providerOptions = useMemo<[string, { label: string; value: string }][]>(
    () =>
      (courseFiltersData?.filters?.providers ?? []).map((p) => [
        p.name,
        { label: p.name, value: p.integrationId ?? '' },
      ]),
    [courseFiltersData]
  );

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  // Which export the confirmation dialog is for; null = closed.
  const [exportType, setExportType] = useState<'csv' | 'certificates' | 'courseProgress' | null>(
    null
  );
  const [coursesDialogOpen, setCoursesDialogOpen] = useState(false);
  const [coursesDialogType, setCoursesDialogType] = useState<StudentCoursesDialogType>('enrolled');
  const [coursesDialogEmployeeId, setCoursesDialogEmployeeId] = useState<string>();
  const [coursesDialogStudentName, setCoursesDialogStudentName] = useState<string>();

  const { exportStudents, isPending: isExportingStudents } = useExportTrainingStudents();

  const { exportCourseProgress, isPending: isExportingCourseProgress } =
    useExportCourseProgressReport();
  const { exportCertificates, isPending: isExportingCertificates } =
    useExportTrainingCertificates();

  const providerId = (filters.provider?.value as string) || undefined;

  // Resolve the four period filters into concrete UTC ISO boundaries once, then spread into every
  // query so list, summary and export stay in sync. getPeriodRange returns {} when a field is unset.
  const dateParams = useMemo(() => {
    const enrolment = getPeriodRange(filters.enrolmentDate);
    const completed = getPeriodRange(filters.completedDate);
    const due = getPeriodRange(filters.dueDate);
    const expiry = getPeriodRange(filters.expiryDate);
    return {
      enrolmentDateFrom: enrolment.from,
      enrolmentDateTo: enrolment.to,
      completedDateFrom: completed.from,
      completedDateTo: completed.to,
      dueDateFrom: due.from,
      dueDateTo: due.to,
      expiryDateFrom: expiry.from,
      expiryDateTo: expiry.to,
    };
  }, [filters.enrolmentDate, filters.completedDate, filters.dueDate, filters.expiryDate]);

  // Chip text for an active period filter, or null when the field carries no usable value (unset, or
  // a custom period missing either date). Presets show their label; custom shows the date range.
  const periodChipLabel = (value: PeriodFilterValue): string | null => {
    if (!value.preset) return null;
    if (value.preset === 'custom') {
      if (!value.startDate || !value.endDate) return null;
      const pattern = getDateFormatPattern(navigator.language);
      return `${format(value.startDate, pattern)} – ${format(value.endDate, pattern)}`;
    }
    return translate(periodPresetLabelKey(value.preset));
  };

  const dateChips = DATE_FILTER_FIELDS.map((field) => ({
    ...field,
    text: periodChipLabel(filters[field.key]),
  })).filter((chip): chip is typeof chip & { text: string } => !!chip.text);

  const { data, isPending: isLoading } = useGetTrainingStudents({
    searchDebounce,
    paginationModel,
    sortModel,
    statuses,
    facultyIds: filters.faculties.map((f) => f.id as string),
    courseIds: filters.courses.map((c) => c.id as string),
    providerId,
    ...dateParams,
  });

  const { data: summaryData, isPending: isSummaryLoading } = useGetStudentStats({
    searchText: searchDebounce,
    facultyIds: filters.faculties.map((f) => f.id as string),
    courseIds: filters.courses.map((c) => c.id as string),
    providerId,
    ...dateParams,
  });

  const students = useMemo(() => data?.students?.data?.map(mapStudentToRow) ?? [], [data]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const sort = model[0];
      track(ManageTrainingEvent.StudentsOrder, {
        column: sort?.field || 'firstName',
        ascending: (sort?.sort || 'asc') === 'asc',
      });
      dispatchManageStudentsFilter({
        type: 'SET_SORT',
        payload: {
          sortBy: sort?.field || 'firstName',
          orderBy: (sort?.sort || 'asc') as 'asc' | 'desc',
        },
      });
    },
    [dispatchManageStudentsFilter]
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      if (isLoading) return;
      dispatchManageStudentsFilter({ type: 'SET_PAGINATION', payload: model });
    },
    [dispatchManageStudentsFilter, isLoading]
  );

  const handleCountBadgeClick = useCallback(
    (_event: React.MouseEvent, row: CourseStudentRow, type: StudentCoursesDialogType) => {
      track(ManageTrainingEvent.StudentsCoursesViewed, { column: type });
      setCoursesDialogEmployeeId(row.employeeId);
      setCoursesDialogStudentName(row.name);
      setCoursesDialogType(type);
      setCoursesDialogOpen(true);
    },
    []
  );

  const handleRemoveFilterItem = (
    key: 'faculties' | 'courses',
    itemId: string | number | null | undefined
  ) => {
    dispatchManageStudentsFilter({
      type: 'SET_FILTERS',
      payload: { ...filters, [key]: filters[key].filter((item) => item.id !== itemId) },
    });
  };

  const handleClearAllFilters = () => {
    dispatchManageStudentsFilter({
      type: 'SET_FILTERS',
      payload: {
        ...filters,
        provider: null,
        faculties: [],
        courses: [],
        enrolmentDate: EMPTY_PERIOD_FILTER,
        completedDate: EMPTY_PERIOD_FILTER,
        dueDate: EMPTY_PERIOD_FILTER,
        expiryDate: EMPTY_PERIOD_FILTER,
      },
    });
  };

  const handleStatCardClick = useCallback(
    (key: string) => {
      const filterActivated = !statuses.includes(key);
      track(ManageTrainingEvent.StudentsQuickFilter, { filterName: key, filterActivated });
      const updated = filterActivated ? [...statuses, key] : statuses.filter((s) => s !== key);
      dispatchManageStudentsFilter({ type: 'SET_STATUSES', payload: updated });
    },
    [statuses, dispatchManageStudentsFilter]
  );

  return (
    <Stack spacing={5}>
      {/* Toolbar */}
      <Box>
        <Grid container spacing={2} alignItems='center'>
          <Grid item xs={12} sm={6} lg={4}>
            <Stack direction='row' alignItems='center' gap={1}>
              <TextField
                value={search}
                dense={true}
                placeholder={translate('common.search')}
                fullWidth={true}
                autoComplete={false}
                leadingIcon={<SearchIcon />}
                onChange={(e) =>
                  dispatchManageStudentsFilter({ type: 'SET_SEARCH', payload: e.target.value })
                }
              />
              <Button
                variant='outlined'
                label={translate('manage.filters')}
                size='md'
                startIcon={<FilterIcon style={{ width: 16, height: 16 }} />}
                sx={{ flex: 'none' }}
                onClick={() => {
                  track(ManageTrainingEvent.StudentsFilter);
                  setFilterDrawerOpen(true);
                }}
              />
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            lg={8}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              gap: 1,
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              gap={1}
              width={{
                xs: '100%',
                sm: 'max-content',
              }}
            >
              {user?.isAllowEnrollStudents ? (
                <Button
                  variant='filled'
                  label={translate('manage.enrollStudents')}
                  size='md'
                  startIcon={<AddIcon style={{ width: 16, height: 16 }} />}
                  sx={{ flex: { xs: 1, sm: 'none' } }}
                  onClick={() => {
                    track(ManageTrainingEvent.StudentsEnrol);
                    const params = new URLSearchParams();
                    filters.courses.forEach((c) => params.append('courseId', c.id as string));
                    const qs = params.toString();
                    navigate(`/settings/learning/enroll-students${qs ? `?${qs}` : ''}`);
                  }}
                />
              ) : null}
              <Button
                variant='outlined'
                label={translate('manage.export')}
                size='md'
                startIcon={<DownloadIcon color={KLAYO_COLORS.NeonBlueNB500} />}
                endIcon={<ChevronDownIcon />}
                sx={{ flex: { xs: 1, md: 'none' } }}
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
              />
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    setExportMenuAnchor(null);
                    setExportType('csv');
                  }}
                >
                  {translate('manage.reportCsv')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setExportMenuAnchor(null);
                    setExportType('certificates');
                  }}
                >
                  {translate('manage.certificatesPdf')}
                </MenuItem>
                {/* Course Progress Report hidden while changes are finalised.
                <MenuItem
                  onClick={() => {
                    setExportMenuAnchor(null);
                    setExportType('courseProgress');
                  }}
                >
                  {translate('manage.courseProgressReportCsv')}
                </MenuItem>
                */}
              </Menu>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Active Filters */}
      {(!!filters.provider?.label ||
        filters.faculties.length > 0 ||
        filters.courses.length > 0 ||
        dateChips.length > 0) && (
        <Stack direction='row' gap={1} flexWrap='wrap' alignItems='center'>
          {filters.provider?.label && (
            <Chip
              label={
                <Stack direction='row' gap={0.5} alignItems='center'>
                  <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR900 }}>
                    {translate('manage.provider')}:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: KLAYO_COLORS.GreyGR900 }}
                    fontWeight={600}
                  >
                    {filters.provider.label}
                  </Typography>
                </Stack>
              }
              onDelete={() =>
                dispatchManageStudentsFilter({
                  type: 'SET_FILTERS',
                  payload: { ...filters, provider: null },
                })
              }
              deleteIcon={<RemoveIcon />}
              variant='outlined'
            />
          )}
          {filters.faculties.map((item: BaseSelectorOption) => (
            <Chip
              key={`faculty-${item.id}`}
              label={
                <Stack direction='row' gap={0.5} alignItems='center'>
                  <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR900 }}>
                    {translate('manage.faculty')}:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: KLAYO_COLORS.GreyGR900 }}
                    fontWeight={600}
                  >
                    {item.label ?? item.name}
                  </Typography>
                </Stack>
              }
              onDelete={() => handleRemoveFilterItem('faculties', item.id)}
              deleteIcon={<RemoveIcon />}
              variant='outlined'
            />
          ))}
          {filters.courses.map((item: BaseSelectorOption) => (
            <Chip
              key={`course-${item.id}`}
              label={
                <Stack direction='row' gap={0.5} alignItems='center'>
                  <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR900 }}>
                    {translate('manage.course')}:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: KLAYO_COLORS.GreyGR900 }}
                    fontWeight={600}
                  >
                    {item.label ?? item.name}
                  </Typography>
                </Stack>
              }
              onDelete={() => handleRemoveFilterItem('courses', item.id)}
              deleteIcon={<RemoveIcon />}
              variant='outlined'
            />
          ))}
          {dateChips.map((chip) => (
            <Chip
              key={`date-${chip.key}`}
              label={
                <Stack direction='row' gap={0.5} alignItems='center'>
                  <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR900 }}>
                    {translate(chip.labelKey)}:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: KLAYO_COLORS.GreyGR900 }}
                    fontWeight={600}
                  >
                    {chip.text}
                  </Typography>
                </Stack>
              }
              onDelete={() =>
                dispatchManageStudentsFilter({
                  type: 'SET_FILTERS',
                  payload: { ...filters, [chip.key]: EMPTY_PERIOD_FILTER },
                })
              }
              deleteIcon={<RemoveIcon />}
              variant='outlined'
            />
          ))}
          <Typography
            variant='body2'
            sx={{ color: KLAYO_COLORS.NeonBlueNB500, cursor: 'pointer', ml: 1 }}
            onClick={handleClearAllFilters}
          >
            {translate('common.clearAll')}
          </Typography>
        </Stack>
      )}

      {/* Stats Cards */}
      <Box>
        <Grid container spacing={2}>
          {STAT_CARDS.map(({ key, labelKey }) => (
            <Grid item xs={6} sm={4} md={2} key={key}>
              <StatCard
                title={translate(labelKey)}
                stat={summaryData?.summary?.[key] ?? 0}
                loading={isSummaryLoading}
                selected={statuses.includes(key)}
                hasActiveSelection={statuses.length > 0}
                onClick={() => handleStatCardClick(key)}
                sx={{ height: '100%', boxSizing: 'border-box' }}
                statStyle={{
                  fontSize: '24px',
                  lineHeight: '32px',
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Students Table / Mobile List */}
      {isSmallScreen ? (
        <StudentAccordions
          rows={students}
          totalCount={data?.students?.totalCount ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          translate={translate}
          isLoading={isLoading}
        />
      ) : (
        <CourseStudentsTable
          rows={students}
          columns={[
            { type: CourseStudentsTable.columns.employee, field: 'firstName' },
            { type: CourseStudentsTable.columns.enrolledCount, field: 'enrolled' },
            { type: CourseStudentsTable.columns.completedCount, field: 'completed' },
            { type: CourseStudentsTable.columns.inProgressCount, field: 'inProgress' },
            { type: CourseStudentsTable.columns.notStartedCount, field: 'notStarted' },
            { type: CourseStudentsTable.columns.failedCount, field: 'failed' },
            { type: CourseStudentsTable.columns.pendingCount, field: 'pending' },
            { type: CourseStudentsTable.columns.overdueCount, field: 'overdue' },
          ]}
          translate={translate}
          sortable={true}
          selectable={false}
          paper={true}
          pagination={true}
          paginationMode='server'
          sortingMode='server'
          totalCount={data?.students?.totalCount ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onCountBadgeClick={handleCountBadgeClick}
          noRowsComponent={
            <TablePlaceholder text={translate('manage.noStudentsFound')} icon={<SearchIcon />} />
          }
          isLoading={isLoading}
          theme={theme}
          keyword={searchDebounce}
        />
      )}

      <StudentCoursesDialog
        open={coursesDialogOpen}
        onClose={() => setCoursesDialogOpen(false)}
        type={coursesDialogType}
        employeeId={coursesDialogEmployeeId}
        studentName={coursesDialogStudentName}
        providerId={providerId}
        facultyIds={filters.faculties.map((f) => f.id as string)}
        courseIds={filters.courses.map((c) => c.id as string)}
        dates={dateParams}
        onLearningHistory={() => track(ManageTrainingEvent.StudentsCoursesViewedHistory)}
        onSendReminderSuccess={(courseName) =>
          track(ManageTrainingEvent.EnrolmentsSendReminder, { courseName })
        }
      />

      <TrainingStudentFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        onApply={(f) => {
          const filterValues: string[] = [
            ...(f.provider ? [String(f.provider.label)] : []),
            ...f.faculties,
            ...f.courses,
          ].map((item) => (typeof item === 'string' ? item : String(item.label ?? item.name)));
          track(ManageTrainingEvent.StudentsFilterSet, { filters: filterValues });
          dispatchManageStudentsFilter({ type: 'SET_FILTERS', payload: f });
        }}
        filters={filters}
        providerOptions={providerOptions}
      />

      {exportType && (
        <ConfirmationDialog
          title={translate('manage.export')}
          question={translate(
            exportType === 'certificates'
              ? 'manage.exportCertificatesDialogMessage'
              : exportType === 'courseProgress'
                ? 'manage.courseProgressExportDialogMessage'
                : 'manage.exportDialogMessage',
            { email: user?.loggedInUserEmail }
          )}
          cancelButton={translate('common.cancel')}
          acceptButton={translate('manage.export')}
          loading={
            exportType === 'certificates'
              ? isExportingCertificates
              : exportType === 'courseProgress'
                ? isExportingCourseProgress
                : isExportingStudents
          }
          onCancel={() => setExportType(null)}
          onAccept={() => {
            const exportParams = {
              searchText: searchDebounce,
              statuses,
              facultyIds: filters.faculties.map((f) => f.id as string),
              courseIds: filters.courses.map((c) => c.id as string),
              providerId,
              ...dateParams,
            };
            const onSuccess = () => setExportType(null);
            if (exportType === 'certificates') {
              track(ManageTrainingEvent.StudentsCertificatesExport);
              exportCertificates(exportParams, { onSuccess });
            } else if (exportType === 'courseProgress') {
              track(ManageTrainingEvent.StudentsCourseProgressExport);
              // The SAME params as its two siblings. Sending only course/faculty exported the whole
              // roster while the screen showed a narrowed list.
              exportCourseProgress(exportParams, { onSuccess } as any);
            } else {
              track(ManageTrainingEvent.StudentsExport);
              exportStudents(exportParams, { onSuccess });
            }
          }}
        />
      )}
    </Stack>
  );
};
